import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import moment from 'moment'

import {
  uploadDocument,
  UploadUserDocumentPayload,
  userUploadDocument,
  UploadError,
} from '@/actions/awsActions'
import { User } from '@/reducers/auth/userReducer'
import { getCurrentUser } from '@/selectors/user.selectors'
import { fetchFinancialAccountsIfNeeded } from '@/actions/financialAccountActions'
import { fetchUserFinancialAccounts } from '@/actions/admin/adminFinancialAccountActions'
import { UploadDocumentType } from '@/constants/businessConstants'
import { parseErrorFromCatch } from '@/utils/errorHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'

import {
  selectUserDocumentCategory,
  selectUserDocumentCategoryByInternalName,
} from '@/featuresV1/Admin/UserDocumentCategories/userDocumentCategories.selectors'
import { useAnalyticsTrack } from '@/featuresV1/Amplitude'
import { fetchUserDocumentCategoriesIfNeeded } from '@/featuresV1/Admin/UserDocumentCategories/userDocumentCategories.slice'
import { fetchUserOnboardingStepsIfNeeded } from '@/featuresV1/Onboarding/UserOnboardingSteps/onboarding.actions'
import { UserDocument } from '@/featuresV1/UserDocuments/userDocuments.slice'
import { TaxFilingUser } from '@/featuresV1/Taxes/AnnualTaxes/annualTaxFilings.slice'
import { DescriptionType } from '@/featuresV1/UserDocuments/constants'
import { UserDocumentCategoryIdentifier } from '@/featuresV1/Admin/UserDocumentCategories/userDocumentCategory.constants'

import { transformMonthYear } from '../lib/helpers'
import { IUploadedDocument } from '../lib/types'
import { StatementData } from '../StatementUploadSection'
import { ContentCard } from './ContentCard'

import { Button, Dialog, Checkbox } from '@/ui'

interface IFileUploadDialog {
  open: boolean
  close: () => void
  documentType: UploadDocumentType
  userFacing: boolean
  user?: User | TaxFilingUser
  categoryId?: number | null
  categoryInternalName?: string
  setUploadedFile?: (_: IUploadedDocument) => Promise<void> | void
  setUploadedFiles?: (_: IUploadedDocument[]) => Promise<void> | void
  taxFilingFormId?: number
  description?: ReactNode
  monthYear?: string // YYYY-MM format
  financialAccountId?: number[]
  subHeaderText?: string
  // Sometimes a file should be uploaded with extra props
  extraPayload?: Partial<UploadUserDocumentPayload>
  // Handles checkbox at bottom of modal that can be used to skip uploading
  overrideCheckboxLabel?: string
  overrideCallback?: () => void
  // The header text displayed at the top of the modal
  modalHeaderText?: string
  hideDialogHeaderText?: boolean
  descriptionType?: DescriptionType | null
  year?: string
  useFilteredAccountOptions?: boolean
  isUploadOnly?: boolean
  taxYearDisabled?: boolean
  isMixedType?: boolean
  internalCategoryNameFlag?: string
  documentCategoryMap?: Map<
    UploadDocumentType,
    UserDocumentCategoryIdentifier[]
  >
  requiredDocumentCount?: number
  uploadedDocumentCount?: number
}

export const DocumentUploadDialog = ({
  open,
  close,
  userFacing,
  documentType: sentDocumentType,
  setUploadedFile,
  setUploadedFiles,
  taxFilingFormId,
  extraPayload,
  user: sentUser,
  categoryId: sentCategoryId,
  categoryInternalName: sentCategoryInternalName,
  monthYear: sentMonthYear,
  financialAccountId: sentFinancialAccountId,
  overrideCheckboxLabel,
  overrideCallback,
  modalHeaderText,
  hideDialogHeaderText,
  year: sentYear,
  isMixedType = false,
  taxYearDisabled,
  internalCategoryNameFlag = '',
  documentCategoryMap,
  requiredDocumentCount,
  uploadedDocumentCount = 0,
}: IFileUploadDialog) => {
  const dispatch = useAppDispatch()
  const [documentType, setDocumentType] = useState(sentDocumentType)
  const [filesToUpload, setFilesToUpload] = useState<File[]>([])
  const [error, setError] = useState('')
  const [uploading, setUploading] = useState(false)
  const [month, setMonth] = useState(transformMonthYear(sentMonthYear)?.month())
  const [statementData, setStatementData] = useState<StatementData[]>([])
  const [taxDocumentType, setTaxDocumentType] = useState<'federal' | 'state'>()
  const [uploadOverride, setUploadOverride] = useState(false)

  const [internalCategoryNames, setInternalCategoryNames] = useState<string[]>(
    []
  )

  const [year, setYear] = useState(
    sentYear ?? transformMonthYear(sentMonthYear)?.year()?.toString()
  )

  const [userDocumentCategoryId, setUserDocumentCategoryId] = useState(
    sentCategoryId ?? undefined
  )

  const [hideDocumentCategoryMixed, setHideDocumentCategoryMixed] =
    useState(false)

  const sentCategoryById = useReselector(
    selectUserDocumentCategory,
    sentCategoryId
  )

  const sentCategoryByName = useReselector(
    selectUserDocumentCategoryByInternalName,
    sentCategoryInternalName
  )

  const sentCategoryByInternalName = useReselector(
    selectUserDocumentCategoryByInternalName,
    internalCategoryNameFlag
  )

  useEffect(() => {
    if (isMixedType) {
      if (documentCategoryMap) {
        const filteredInternalNames = documentCategoryMap.get(documentType)

        if ((filteredInternalNames?.length ?? 0) > 0) {
          setInternalCategoryNames(filteredInternalNames ?? [])
          setUserDocumentCategoryId(undefined)
          setHideDocumentCategoryMixed(false)
        } else {
          setUserDocumentCategoryId(sentCategoryByInternalName?.id ?? undefined)
          setHideDocumentCategoryMixed(true)
        }
      }
    }
  }, [
    documentCategoryMap,
    documentType,
    isMixedType,
    sentCategoryByInternalName?.id,
  ])

  const sentCategory = sentCategoryById || sentCategoryByName
  const currentUser = useReselector(getCurrentUser)
  const track = useAnalyticsTrack()
  const user = sentUser || currentUser

  useEffect(() => {
    setDocumentType(sentDocumentType)
  }, [sentDocumentType])

  // If the category id prop changes update the local state
  useEffect(() => {
    setUserDocumentCategoryId(sentCategory?.id ?? undefined)
  }, [sentCategory?.id])

  // If a sent month is sent update year and month local state
  useEffect(() => {
    if (!sentYear) {
      setYear(transformMonthYear(sentMonthYear)?.year()?.toString())
      setMonth(transformMonthYear(sentMonthYear)?.month())
    }
  }, [sentMonthYear, sentYear])

  // if Bank Statement modal fetch financial account information when needed
  useEffect(() => {
    if (documentType === UploadDocumentType.STATEMENT) {
      if (userFacing) {
        dispatch(fetchFinancialAccountsIfNeeded())
      } else if (user) {
        dispatch(fetchUserFinancialAccounts(user.id, true))
      }
    }
  }, [documentType, userFacing, user, dispatch])

  useEffect(() => {
    dispatch(fetchUserDocumentCategoriesIfNeeded())
    dispatch(
      fetchUserOnboardingStepsIfNeeded({
        expanded: true,
      })
    )
  }, [dispatch])

  const uploadFiles = useCallback(async () => {
    if (!user?.id) {
      return
    }

    const uploadFile = async (
      file: File,
      index: number
    ): Promise<IUploadedDocument | null> => {
      let docRes: UserDocument | UploadError | null = null

      const statementMonth =
        year !== undefined && month !== undefined
          ? moment().year(Number(year)).month(month)
          : undefined

      const payload = {
        file,
        user: {
          id: user.id,
          uuid: user.uuid,
        },
        documentType,
        statementMonth,
        userDocumentCategoryId,
        year:
          documentType === UploadDocumentType.TAX && year ? year : undefined,
        ...extraPayload,
      }

      if (statementData[index]) {
        const statement = {
          statementMonth: statementData[index].statementMonth,
          financialAccountIds: statementData[index].financialAccountIds,
        }

        payload.statement = statement
      }

      if (userFacing) {
        const res = await userUploadDocument(payload)(dispatch)

        if (res && 'userDocument' in res) {
          docRes = res.userDocument
        } else if (res && 'error' in res) {
          docRes = res
        }
      } else {
        docRes = await uploadDocument(payload)(dispatch)
      }

      if (docRes && 'error' in docRes) {
        throw new Error(docRes.error)
      }

      if (docRes && 'id' in docRes) {
        return {
          title: docRes.fileDescription || '',
          url: docRes.signedUrl || '',
          id: docRes.id,
          statementMonth,
          taxDocumentType,
          fileName: file.name,
          doc: docRes,
        }
      }

      return null
    }

    setError('')
    setUploading(true)

    try {
      const results = await Promise.all(
        filesToUpload.map((file, index) => uploadFile(file, index))
      )

      const successfulUploads = results.filter(
        (r): r is IUploadedDocument => r !== null
      )

      const uploadedFiles: IUploadedDocument[] = successfulUploads.map(
        (result) => ({
          ...result,
        })
      )

      if (setUploadedFiles) {
        await setUploadedFiles(uploadedFiles)
      } else if (setUploadedFile && uploadedFiles.length > 0) {
        await setUploadedFile(uploadedFiles[0])
      }

      setFilesToUpload([])
      close()
    } catch (err) {
      setError(parseErrorFromCatch(err))
    } finally {
      setUploading(false)
    }
  }, [
    user?.id,
    user?.uuid,
    year,
    month,
    documentType,
    userDocumentCategoryId,
    extraPayload,
    userFacing,
    setUploadedFile,
    setUploadedFiles,
    dispatch,
    filesToUpload,
    close,
    taxDocumentType,
    statementData,
  ])

  const onUploadButtonClicked = useCallback(() => {
    const trackProperties: {
      document_category: string
      document_name: string
      document_subcategory: string
      report_month?: number
      report_year?: number
      tax_year?: number
    } = {
      document_category: documentType,
      document_subcategory: sentCategory?.id.toString() || '',
      document_name: sentCategory?.title || '',
    }

    if (year) {
      if (documentType === UploadDocumentType.STATEMENT) {
        trackProperties.report_year = Number(year)
      } else {
        trackProperties.tax_year = Number(year)
      }
    }

    if (month) {
      trackProperties.report_month = month
    }

    track('clicked document upload', trackProperties)

    if (uploadOverride) {
      overrideCallback?.()
      close()
    } else {
      uploadFiles()
    }
  }, [
    close,
    overrideCallback,
    uploadFiles,
    uploadOverride,
    track,
    sentCategory,
    documentType,
    month,
    year,
  ])

  const isUploadDisabled = useMemo(() => {
    // If upload override checkbox checked then enabled
    if (uploadOverride) {
      return false
    }

    // If no files are uploaded then disabled
    if (filesToUpload.length === 0) {
      return true
    }

    if (
      documentType === UploadDocumentType.STATEMENT &&
      !statementData?.length
    ) {
      return true
    }

    if (
      documentType === UploadDocumentType.STATEMENT &&
      !statementData.every((item) => item.statementMonth)
    ) {
      return true
    }

    return Boolean(
      uploading ||
        (requiredDocumentCount &&
          uploadedDocumentCount >= requiredDocumentCount)
    )
  }, [
    documentType,
    filesToUpload.length,
    uploadOverride,
    statementData,
    uploading,
    requiredDocumentCount,
    uploadedDocumentCount,
  ])

  if (!open) {
    return null
  }

  return (
    <Dialog open={open} onClose={close}>
      <Dialog.Title>
        {!hideDialogHeaderText && (modalHeaderText || 'Upload documents')}
      </Dialog.Title>

      <Dialog.Content>
        <ContentCard
          documentCategoryMap={documentCategoryMap}
          documentType={documentType}
          error={error}
          hideDocumentCategoryMixed={hideDocumentCategoryMixed}
          internalCategoryNameFlag={internalCategoryNameFlag}
          internalCategoryNames={internalCategoryNames}
          sentCategoryId={sentCategoryId}
          sentFinancialAccountId={sentFinancialAccountId}
          sentMonthYear={sentMonthYear}
          sentYear={sentYear}
          setDocumentType={setDocumentType}
          setError={setError}
          setHideDocumentCategoryMixed={setHideDocumentCategoryMixed}
          setInternalCategoryNames={setInternalCategoryNames}
          setMonth={setMonth}
          setStatementData={setStatementData}
          setTaxDocumentType={setTaxDocumentType}
          setUploadOverride={setUploadOverride}
          setYear={setYear}
          statementData={statementData}
          taxDocumentType={taxDocumentType}
          taxFilingFormId={taxFilingFormId}
          taxYearDisabled={taxYearDisabled}
          user={sentUser}
          userFacing={userFacing}
        />

        {overrideCheckboxLabel && !filesToUpload.length && (
          <Checkbox
            label={overrideCheckboxLabel}
            onCheckedChange={(checked) => setUploadOverride(checked)}
            checked={uploadOverride}
          />
        )}
      </Dialog.Content>

      <Dialog.Footer>
        <Dialog.Close>
          <Button variant="secondary">Cancel</Button>
        </Dialog.Close>

        <Button
          onClick={onUploadButtonClicked}
          loading={uploading}
          disabled={isUploadDisabled}
        >
          {uploadOverride
            ? 'Submit'
            : `Upload${filesToUpload.length > 0 ? ` (${filesToUpload.length})` : ''}`}
        </Button>
      </Dialog.Footer>
    </Dialog>
  )
}
